import { Component, forwardRef, Input, OnDestroy, OnInit } from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    FormBuilder,
    FormGroup,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator,
} from '@angular/forms';
import { NgbDateAdapter, NgbDateStruct } from '@cmps/ng-bootstrap';
import { isValid } from 'date-fns';
import { tap } from 'rxjs/operators';

import {
    takeUntilDestroy,
    TakeUntilDestroy,
} from '../../take-until-destroy.decorator';

import { DateTimeProviders } from './datetime-providers';
import { validate } from './datetime-validator';

@Component({
    selector: 'cmps-datetime-picker',
    template: ` <div
        [class.cmps-datetime-picker--fill-width]="fillWidth"
        [formGroup]="_form"
    >
        <ng-container *ngIf="!onlyTime">
            <ngb-datepicker
                [minDate]="_minDateStruct"
                [maxDate]="_maxDateStruct"
                formControlName="date"
            ></ngb-datepicker>
        </ng-container>
        <ng-container *ngIf="!onlyDate">
            <div
                class="d-flex flex-column align-items-center cmps-datetime-picker--border"
                [class.cmps-datetime-picker--date-time-border]="!onlyTime"
            >
                <ngb-timepicker
                    [meridian]="true"
                    formControlName="time"
                ></ngb-timepicker>
            </div>
        </ng-container>
    </div>`,
    host: {
        '(blur)': '_onBlur()',
    },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DateTimePickerComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => DateTimePickerComponent),
            multi: true,
        },
        DateTimeProviders,
    ],
})
@TakeUntilDestroy()
export class DateTimePickerComponent
    implements OnInit, OnDestroy, ControlValueAccessor, Validator
{
    /** show only the calendar control */
    @Input() onlyDate: boolean;

    /** show only the time control */
    @Input() onlyTime: boolean;

    /** whether to stretch the calendar horizontally to fit its container */
    @Input() fillWidth: boolean;

    /** @private */
    _form: FormGroup;

    private _onChange: (_: Date) => void;
    private _onTouched: () => void;
    private _onValidatorChange: () => void;

    /** @private */
    _minDate: Date;
    /** @private */
    _minDateStruct: NgbDateStruct;

    /** min date for navigation */
    // eslint-disable-next-line accessor-pairs
    @Input()
    set minDate(value: Date) {
        this._minDate = value;
        this._minDateStruct = this._ngbDateAdapter.fromModel(value);
    }

    /** @private */
    _maxDate: Date;
    /** @private */
    _maxDateStruct: NgbDateStruct;

    /** max date for navigation */
    // eslint-disable-next-line accessor-pairs
    @Input()
    set maxDate(value: Date) {
        this._maxDate = value;
        this._maxDateStruct = this._ngbDateAdapter.fromModel(value);
    }

    constructor(
        private readonly _formBuilder: FormBuilder,
        private readonly _ngbDateAdapter: NgbDateAdapter<Date>,
    ) {}

    ngOnInit(): void {
        const date = new Date();
        this._form = this._formBuilder.group({
            date: [date],
            time: [date],
        });
        this._form.valueChanges
            .pipe(
                tap((value: { date: Date; time: Date }) => {
                    if (!this._onChange) {
                        return;
                    }
                    const dDate = !isValid(value.date)
                        ? new Date()
                        : value.date;
                    const tDate = !isValid(value.time)
                        ? new Date()
                        : value.time;
                    const result = new Date(
                        dDate.getFullYear(),
                        dDate.getMonth(),
                        dDate.getDate(),
                        tDate.getHours(),
                        tDate.getMinutes(),
                        0,
                    );
                    this._onChange(result);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        //
    }

    registerOnChange(fn: any): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this._onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        isDisabled
            ? this._form.disable({ onlySelf: true, emitEvent: false })
            : this._form.enable({ onlySelf: true, emitEvent: false });
    }

    writeValue(obj: Date): void {
        const date = !isValid(obj) ? new Date() : obj;
        this._form.patchValue(
            { date, time: date },
            { onlySelf: true, emitEvent: false },
        );
    }

    registerOnValidatorChange(fn: () => void): void {
        this._onValidatorChange = fn;
    }

    validate(control: AbstractControl): ValidationErrors | null {
        const { value } = control;
        return validate(value, this._minDate, this._maxDate);
    }

    /** @private */
    _onBlur(): void {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._onTouched && this._onTouched();
    }
}
